import { InHouseSocialLoginState } from '../reducers/create-account-reducer';
import { RootState, useAppSelector } from '../stores/redux.store';
import { ConfirmationErrorCode, ConfirmationFieldError, OidcContext, UserFlow } from '../types';
import { InHouseSocialLoginType } from '../utilities/social-login';
import { MarketingConsentConfig } from '../reducers/marketing-consent-reducer';
import { createSelector } from '@reduxjs/toolkit';

type PageState = {
  formError: ConfirmationErrorCode | undefined;
  fieldErrors?: ConfirmationFieldError;
  isEmbedded: boolean;
  oidcContext?: OidcContext;
  marketingConsentConfig?: MarketingConsentConfig;
  email: string;
  displayName: string;
  userFlow?: UserFlow;
  source?: string;
  redirectTo: string | null;
  isLoading: boolean | undefined;
  createNeeded: boolean;
  verifyNeeded: boolean;
  recaptchaEnterpriseInvisibleKeySite: string;

  loginType: InHouseSocialLoginType;
  mfaToken?: string;
  confirmationToken: string;
  hashedCsrfToken: string;
};

const selectMarketingConsent = (state: RootState) => state.marketingConsent;
const selectHashedCsrfToken = (state: RootState) => state.hashedCsrfToken;
const selectMicrobranding = (state: RootState) => state.microbranding;
const selectAppConfig = (state: RootState) => state.appConfig;
const selectCreateAccount = (state: RootState) => state.createAccount;

export const useCreateAccountConfirmationPageState = (): PageState => {
  return useAppSelector(
    createSelector(
      [
        selectMarketingConsent,
        selectHashedCsrfToken,
        selectMicrobranding,
        selectAppConfig,
        selectCreateAccount,
      ],
      (marketingConsent, hashedCsrfToken, microbranding, appConfig, createAccount) => {
        const { config: marketingConsentConfig } = marketingConsent;
        const { recaptchaEnterpriseInvisibleKeySite } = appConfig;
        const { formError, fieldErrors, redirectTo, isLoading } = createAccount;
        const { isEmbedded, oidcContext } = microbranding;

        const baseState = {
          formError,
          fieldErrors,
          marketingConsentConfig,
          redirectTo,
          isLoading,
          isEmbedded,
          oidcContext,
          recaptchaEnterpriseInvisibleKeySite,
        };

        const {
          loginType,
          displayName,
          email,
          userFlow,
          confirmationToken,
          mfaToken,
          createNeeded,
          verifyNeeded,
        } = createAccount as InHouseSocialLoginState;
        return {
          ...baseState,
          loginType,
          displayName,
          email,
          createNeeded,
          verifyNeeded,
          userFlow,
          confirmationToken,
          mfaToken,
          hashedCsrfToken,
        };
      }
    )
  );
};
